import React from 'react';
import {
  ArrowRight,
  Shield,
  Clock,
  CreditCard,
  Ban,
  HelpCircle,
  Layers,
  RefreshCw,
  TrendingUp,
  ClipboardList,
  Sparkles,
  CheckCircle2,
  XCircle,
  Lightbulb,
  Target,
  FileText,
  Zap,
  Users,
  Briefcase,
  Building2,
  Rocket
} from 'lucide-react';
import Logo from '../Logo';
import { SEO } from '../SEO';
import { Button } from '../ui/Button';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'wouter';
import { SupportFooter } from '../SupportFooter';

const painPoints = [
  {
    icon: RefreshCw,
    title: "Every article recommends different tools",
    description: "One says Notion, another says Asana, another says Monday. You're no closer to a decision."
  },
  {
    icon: HelpCircle,
    title: "Most advice assumes you're technical",
    description: "Jargon-filled comparisons that don't help if you just want to run your business."
  },
  {
    icon: Layers,
    title: "Software overlaps and does the same things",
    description: "Half these tools have identical features. How do you know which one is actually right?"
  },
  {
    icon: CreditCard,
    title: "Choosing wrong means wasted money",
    description: "Lock into the wrong tool and you're stuck with painful migrations and lost time later."
  },
  {
    icon: TrendingUp,
    title: "You don't know what you need now vs later",
    description: "Some tools are overkill today. Others you'll outgrow in 6 months. Which is which?"
  }
];

const blueprintComponents = [
  {
    icon: Target,
    name: "Core Stack",
    description: "What to use right now",
    detail: "The essential tools matched to your exact business stage and needs"
  },
  {
    icon: TrendingUp,
    name: "Growth Stack",
    description: "What to add as your business grows",
    detail: "Sequenced recommendations for your next phase"
  },
  {
    icon: XCircle,
    name: "Avoid List",
    description: "Tools to skip so you don't overpay",
    detail: "Save money by knowing what you don't need"
  },
  {
    icon: FileText,
    name: "Plain-English Explanations",
    description: "Why each tool fits your situation",
    detail: "No jargon - just clear reasoning"
  },
  {
    icon: Zap,
    name: "Upgrade Path",
    description: "When and how to level up",
    detail: "Clear triggers for when to change or expand"
  }
];

const steps = [
  {
    icon: ClipboardList,
    title: "Answer a few questions about your business",
    description: "Takes about 3 minutes"
  },
  {
    icon: Sparkles,
    title: "We analyze what actually fits your situation",
    description: "Matched against 100+ tools"
  },
  {
    icon: CheckCircle2,
    title: "Get a clear software plan you can act on immediately",
    description: "Your complete Blueprint"
  }
];

const whoThisIsFor = [
  "People starting a new business",
  "Founders who don't want to choose wrong",
  "Anyone overwhelmed by software research",
  "Small businesses that want clarity, not complexity"
];

const differentiators = [
  {
    icon: XCircle,
    text: "No generic \"top tools\" lists"
  },
  {
    icon: Shield,
    text: "No paid placements in recommendations"
  },
  {
    icon: Ban,
    text: "Tells you what NOT to use"
  },
  {
    icon: RefreshCw,
    text: "Built to reduce regret and rework"
  },
  {
    icon: CreditCard,
    text: "One-time payment - no subscriptions"
  }
];

const faqItems = [
  {
    question: "What is the Business Software Blueprint?",
    answer: "A personalized software plan that includes your recommended tools, what to avoid, and a clear upgrade path as your business grows."
  },
  {
    question: "How long does it take?",
    answer: "About 3 minutes to complete the questionnaire, then instant access to your full Blueprint."
  },
  {
    question: "What if it doesn't work for me?",
    answer: "If you don't feel 100% confident in your software decisions after using btoolme, we'll refund you - no questions asked."
  }
];

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Get Your Business Software Blueprint",
  "description": "Get a personalized software strategy for your business in minutes",
  "step": [
    {
      "@type": "HowToStep",
      "position": 1,
      "name": "Answer Questions",
      "text": "Answer a few questions about your business - takes about 3 minutes"
    },
    {
      "@type": "HowToStep",
      "position": 2,
      "name": "Analysis",
      "text": "We analyze what actually fits your situation against 100+ tools"
    },
    {
      "@type": "HowToStep",
      "position": 3,
      "name": "Get Blueprint",
      "text": "Get a clear software plan you can act on immediately"
    }
  ],
  "totalTime": "PT3M"
};

export function WhatSoftwareLandingPage() {
  const [_, navigate] = useLocation();

  const seoTitle = "What Software Do I Need for My Business? | btoolme";
  const seoDescription = "Not sure what software your business needs? btoolme builds a personalized software blueprint based on your goals, budget, and stage - so you stop researching and start building.";

  const guaranteeText = "If you don't feel 100% confident in your software decisions after using btoolme, we'll refund you - no questions asked.";

  return (
    <div className="min-h-screen flex flex-col" id="main-content">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path="/what-software-do-i-need-for-my-business"
        faqItems={faqItems}
        keywords="what software do I need for my business, business software needs, software for new business, essential business software, business tool requirements, software recommendations, business software guide, startup software needs, software for entrepreneurs, which software for business, business software checklist, software selection guide"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-gray-50/50 to-white">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-slate-200/30 rounded-full blur-3xl" />
          <div className="absolute top-20 -left-20 w-60 h-60 bg-gray-100/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-100/40 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <Logo className="mb-8 mx-auto" />

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6">
              What Software Do I Need for My Business?
            </h1>

            <p className="text-xl sm:text-2xl text-gray-600 mb-10 font-medium max-w-3xl mx-auto leading-relaxed">
              If you're starting a business and overwhelmed by software choices, btoolme builds a clear software blueprint tailored to your business — so you don't waste time or money choosing the wrong tools.
            </p>

            <div className="flex flex-col items-center gap-4">
              <Button
                onClick={() => navigate('/questionnaire')}
                className="group px-8 py-4 text-lg sparkle-button w-full sm:w-auto"
                size="lg"
                aria-label="Build your personalized software blueprint"
              >
                Build My Software Blueprint
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>

              <p className="text-sm text-gray-600 max-w-md text-center leading-relaxed">
                {guaranteeText}
              </p>
            </div>

            <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-600" />
                <span>3-minute questionnaire</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-slate-600" />
                <span>One-time $29</span>
              </div>
              <div className="flex items-center gap-2">
                <Ban className="w-4 h-4 text-slate-600" />
                <span>No subscriptions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Why This Question Is So Hard to Answer
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              You've probably spent hours researching already. Here's why you're still stuck.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {painPoints.slice(0, 4).map((point, index) => {
              const Icon = point.icon;
              return (
                <div
                  key={index}
                  className="flex gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{point.title}</h3>
                    <p className="text-sm text-gray-600">{point.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 gap-5 mt-5 max-w-md mx-auto sm:max-w-lg">
            {painPoints.slice(4).map((point, index) => {
              const Icon = point.icon;
              return (
                <div
                  key={index}
                  className="flex gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{point.title}</h3>
                    <p className="text-sm text-gray-600">{point.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reframe Section */}
      <section className="py-16 sm:py-20 bg-slate-50 border-y border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-200 rounded-2xl mb-6">
            <Lightbulb className="w-8 h-8 text-slate-700" />
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            The Problem Isn't Software — It's Decision Overload
          </h2>

          <p className="text-xl text-gray-700 font-medium mb-6">
            Most people don't need more tools. They need fewer, better decisions.
          </p>

          <p className="text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Different businesses need different setups. What works for one founder won't work for another.
            The right software stack depends on your goals, your budget, your team size, and where you are right now.
            That's why generic "best tools" lists don't help — they're not built for you.
          </p>
        </div>
      </section>

      {/* Blueprint Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Your Business Software Blueprint
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Instead of guessing or comparing endless tools, btoolme creates a personalized software plan for your business.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {blueprintComponents.slice(0, 3).map((component, index) => {
              const Icon = component.icon;
              return (
                <div
                  key={index}
                  className="bg-gray-50 rounded-xl border border-gray-200 p-6 hover:border-gray-300 transition-colors"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm border border-gray-100">
                    <Icon className="w-6 h-6 text-slate-700" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-2">{component.name}</h3>
                  <p className="text-gray-600 text-sm mb-2">{component.description}</p>
                  <p className="text-slate-700 text-sm font-medium">{component.detail}</p>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5 max-w-2xl mx-auto lg:max-w-none lg:px-24">
            {blueprintComponents.slice(3).map((component, index) => {
              const Icon = component.icon;
              return (
                <div
                  key={index}
                  className="bg-gray-50 rounded-xl border border-gray-200 p-6 hover:border-gray-300 transition-colors"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm border border-gray-100">
                    <Icon className="w-6 h-6 text-slate-700" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-2">{component.name}</h3>
                  <p className="text-gray-600 text-sm mb-2">{component.description}</p>
                  <p className="text-slate-700 text-sm font-medium">{component.detail}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-gray-50 border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative flex flex-col items-center text-center">
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-slate-300 to-transparent" />
                  )}

                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-slate-600 to-slate-700 rounded-2xl flex items-center justify-center shadow-lg shadow-slate-500/20">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-slate-900 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {index + 1}
                    </div>
                  </div>

                  <h3 className="mt-5 text-lg font-semibold text-gray-900 max-w-[200px]">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-gray-500 text-sm">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Who This Is For
            </h2>
          </div>

          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 sm:p-8">
            <ul className="space-y-4">
              {whoThisIsFor.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 pt-6 border-t border-gray-200 text-sm text-gray-500 text-center">
              Works for online businesses, service businesses, trades, freelancers, and more.
            </p>
          </div>
        </div>
      </section>

      {/* Differentiation Section */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              How btoolme Is Different
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {differentiators.slice(0, 3).map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200"
                >
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-slate-700" />
                  </div>
                  <span className="text-gray-800 font-medium text-sm">{item.text}</span>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 max-w-2xl mx-auto">
            {differentiators.slice(3).map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200"
                >
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-slate-700" />
                  </div>
                  <span className="text-gray-800 font-medium text-sm">{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-slate-600/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Stop Researching. Start Building.
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Get your personalized Business Software Blueprint and move forward with confidence.
          </p>

          <div className="flex flex-col items-center gap-4">
            <Button
              onClick={() => navigate('/questionnaire')}
              className="group px-10 py-5 text-lg bg-white text-gray-900 hover:bg-gray-100 font-semibold rounded-xl shadow-xl shadow-black/20 hover:shadow-2xl hover:shadow-black/30 transition-all duration-300 hover:scale-[1.02]"
              size="lg"
            >
              Build My Software Blueprint
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>

            <p className="text-sm text-gray-400 max-w-md text-center mt-2">
              {guaranteeText}
            </p>
          </div>

          <p className="mt-10 text-sm text-gray-500">
            Questions? <a href="mailto:btoolmepro@gmail.com" className="text-gray-400 hover:text-white underline">Contact us</a> — we're happy to help.
          </p>
        </div>
      </section>

      <SupportFooter variant="dark" className="bg-slate-900" />
    </div>
  );
}
